import React, { useEffect, useRef, useState, Suspense, lazy } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import { useContent } from "./cms/ContentContext";
import { submitForm } from "./cms/api";
import SiteFooter from "./pages/SiteFooter";
import "./App.css";

const GradientBlinds = lazy(() => import("./GradientBlinds"));
const BackgroundCanvas = lazy(() => import("./effects/BackgroundCanvas"));

/** CMS still stores .png URLs; serve a sibling .webp when present. GIFs stay as-is (too large to transcode in this pass). */
function webpSibling(src) {
  return typeof src === "string" && /\.png$/i.test(src)
    ? src.replace(/\.png$/i, ".webp")
    : null;
}

/* Flow-diagram icons keyed for CMS (visuals stay identical) */
const FLOW_ICONS = {
  crude: (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
      <path d="M12 3C12 3 6 11 6 15.5a6 6 0 0012 0C18 11 12 3 12 3z" />
    </svg>
  ),
  base: (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.6">
      <circle cx="12" cy="12" r="8" />
      <circle cx="12" cy="12" r="2.5" />
      <line x1="12" y1="4" x2="12" y2="7" strokeWidth="1.2" />
      <line x1="12" y1="17" x2="12" y2="20" strokeWidth="1.2" />
      <line x1="4" y1="12" x2="7" y2="12" strokeWidth="1.2" />
      <line x1="17" y1="12" x2="20" y2="12" strokeWidth="1.2" />
    </svg>
  ),
  lubricant: (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
      <path d="M12 3C12 3 6 11 6 15.5a6 6 0 0012 0C18 11 12 3 12 3z" />
      <path d="M12 18.5a3 3 0 01-3-3" strokeWidth="1.2" opacity="0.5" />
    </svg>
  ),
  used: (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
      <path d="M12 3C12 3 6 11 6 15.5a6 6 0 0012 0C18 11 12 3 12 3z" opacity="0.5" />
      <line x1="9" y1="9" x2="15" y2="15" strokeWidth="1.3" />
      <line x1="15" y1="9" x2="9" y2="15" strokeWidth="1.3" />
    </svg>
  ),
};

/* ─────────────────────────────────────────────
   PIXI PARTICLES — Orbital swarm around coal center
   Depth-sorted: back particles faint, front particles bright
   Front particles render above coal (z=4), back faded out visually
───────────────────────────────────────────── */
function PixiBackground() {
  const wrapRef = useRef(null);
  const appRef = useRef(null);
  const hiddenRef = useRef(false);
  const [hidden, setHidden] = useState(false);
  const [ready, setReady] = useState(false);

  hiddenRef.current = hidden;

  // Parent already waits for user input before mounting this; boot Pixi on the next frame.
  useEffect(() => {
    let rafId = requestAnimationFrame(() => setReady(true));
    return () => cancelAnimationFrame(rafId);
  }, []);

  useEffect(() => {
    const app = appRef.current;
    if (!app?.ticker) return undefined;
    if (hidden) app.ticker.stop();
    else app.ticker.start();
    return undefined;
  }, [hidden]);

  useEffect(() => {
    const layerSection = document.querySelector(".layer-section");
    if (!layerSection || typeof IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver(
      ([entry]) => setHidden(Boolean(entry?.isIntersecting)),
      { root: null, threshold: 0 }
    );
    observer.observe(layerSection);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!ready || !wrapRef.current) return;
    let app;
    let destroyed = false;
    let cleanupResize;

    const boot = async () => {
      const { Application, Graphics, Container } = await import("pixi.js");
      if (destroyed) return;

      app = new Application();
      await app.init({
        width:           window.innerWidth,
        height:          window.innerHeight,
        backgroundAlpha: 0,
        antialias:       false,
        resolution:      1,
        autoDensity:     true,
      });
      if (destroyed) { app.destroy(true); return; }

      appRef.current = app;
      if (hiddenRef.current) app.ticker.stop();

      Object.assign(app.canvas.style, {
        position: "fixed", inset: "0",
        width: "100%", height: "100%",
        pointerEvents: "none",
      });
      wrapRef.current.appendChild(app.canvas);

      const stage = new Container();
      app.stage.addChild(stage);

      const W = () => app.renderer.width;
      const H = () => app.renderer.height;

      // Coal center — matches CoalImageCanvas centering
      const cx = () => W() * 0.50;
      const cy = () => H() * 0.50;

      // vw in px for orbit radius scaling
      const vw = () => window.innerWidth / 100;

      const N = 180;

      const pts = Array.from({ length: N }, () => {
        // Orbit radius: 0.5–24 vw — covers center through full coal face
        const orbitRadius  = (Math.random() * 34 + 0.5) * vw();
        const angle        = Math.random() * Math.PI * 2;
        // Mix of CW and CCW orbits
        const dir          = Math.random() < 0.5 ? 1 : -1;
        const speed        = dir * (Math.random() * 0.001584 + 0.00044);
        const baseSize     = (Math.random() * 2.0 + 0.5) * (0.8781 * (0.90 + Math.random() * 0.25));
        const baseAlpha    = Math.random() * 0.28 + 0.06;
        // Y-axis sine oscillation for volumetric feel
        const yOscAmp      = (Math.random() * 48 + 20) * (vw() / 14);
        const yOscFreq     = Math.random() * 0.0008 + 0.0003;
        const yOscPhase    = Math.random() * Math.PI * 2;

        const g = new Graphics();
        g.circle(0, 0, baseSize);
        g.fill(0xc8d4e0);
        g.alpha = baseAlpha;
        g.x = cx() + Math.cos(angle) * orbitRadius;
        g.y = cy() + Math.sin(angle) * orbitRadius;
        stage.addChild(g);

        return { g, angle, orbitRadius, speed, baseSize, baseAlpha, yOscAmp, yOscFreq, yOscPhase, rx: 0, ry: 0 };
      });

      let mouseX = window.innerWidth / 2, mouseY = window.innerHeight / 2;
      const onMouseMove = (e) => { mouseX = e.clientX; mouseY = e.clientY; };
      window.addEventListener("mousemove", onMouseMove);

      const REPEL_R = 90;
      const REPEL_F = 18;
      let elapsed = 0;
      let currentSizeScale = 1;

      app.ticker.add((ticker) => {
        elapsed += ticker.deltaTime;
        const ocx = cx(), ocy = cy();

        // Scroll-driven size: 100%→50% over the first fold, lerped
        const targetSizeScale = 1 - Math.min(1, window.scrollY / window.innerHeight) * 0.5;
        currentSizeScale += (targetSizeScale - currentSizeScale) * 0.06;

        for (const p of pts) {
          // Advance orbital angle
          p.angle += p.speed * ticker.deltaTime;

          // Compute raw orbit position
          const ox = ocx + Math.cos(p.angle) * p.orbitRadius;
          // Y oscillation layered on top of the flat orbit plane
          const oy = ocy + Math.sin(p.angle) * p.orbitRadius * 0.62
                         + Math.sin(elapsed * p.yOscFreq + p.yOscPhase) * p.yOscAmp;

          // Mouse repulsion — lerped for smooth organic movement
          const mdx   = ox - mouseX;
          const mdy   = oy - mouseY;
          const mdist = Math.hypot(mdx, mdy);
          let targetRX = 0, targetRY = 0;
          if (mdist < REPEL_R && mdist > 0.5) {
            const force = ((REPEL_R - mdist) / REPEL_R) * REPEL_F;
            targetRX = (mdx / mdist) * force;
            targetRY = (mdy / mdist) * force;
          }
          p.rx += (targetRX - p.rx) * 0.07;
          p.ry += (targetRY - p.ry) * 0.07;

          p.g.x = ox + p.rx;
          p.g.y = oy + p.ry;

          // Subtle depth cue — narrow range so upper half stays visible
          const depthFactor = (Math.sin(p.angle) + 1) / 2; // 0..1

          p.g.alpha = p.baseAlpha * (0.55 + depthFactor * 0.45);

          const sc = (0.75 + depthFactor * 0.25) * currentSizeScale;
          p.g.scale.set(sc);
        }
      });

      const onResize = () => app.renderer.resize(window.innerWidth, window.innerHeight);
      window.addEventListener("resize", onResize);
      cleanupResize = () => {
        window.removeEventListener("resize", onResize);
        window.removeEventListener("mousemove", onMouseMove);
      };
    };

    boot();
    return () => {
      destroyed = true;
      cleanupResize?.();
      appRef.current = null;
      try { app?.destroy(true, { children: true }); } catch (_) {}
    };
  }, [ready]);

  return (
    <div
      ref={wrapRef}
      style={{ position: "fixed", inset: 0, zIndex: 4, pointerEvents: "none", opacity: hidden ? 0 : 1, transition: "opacity 0.4s ease" }}
    />
  );
}

/* ─────────────────────────────────────────────
   COAL IMAGE — lightweight canvas (deferred; no perpetual rAF grain)
───────────────────────────────────────────── */
function CoalImageCanvas() {
  const canvasRef  = useRef(null);
  const wrapperRef = useRef(null);
  const { content } = useContent();
  const coalImage = content?.site?.coalImage || "/coal-bg.png";

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d", { alpha: true });
    const W = canvas.width;
    const H = canvas.height;
    let cancelled = false;

    const img = new Image();
    img.decoding = "async";
    const webp = webpSibling(coalImage);
    if (webp) {
      img.onerror = () => {
        img.onerror = null;
        img.src = coalImage;
      };
      img.src = webp;
    } else {
      img.src = coalImage;
    }
    img.onload = () => {
      if (cancelled) return;

      // Process off-main-path via a small timeout so first paint isn't blocked
      const run = () => {
        if (cancelled) return;
        const off = document.createElement("canvas");
        off.width = W;
        off.height = H;
        const octx = off.getContext("2d");
        octx.drawImage(img, 0, 0, W, H);
        const id = octx.getImageData(0, 0, W, H);
        const d = id.data;
        for (let i = 0; i < d.length; i += 4) {
          const lum = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
          if (lum > 135) d[i + 3] = 0;
          else if (lum > 55) d[i + 3] = ((135 - lum) / 80 * 255) | 0;
        }
        octx.putImageData(id, 0, 0);

        // Single static grain pass (no 10-frame bake, no animation loop)
        const grain = document.createElement("canvas");
        grain.width = W;
        grain.height = H;
        const gctx = grain.getContext("2d");
        const gid = gctx.createImageData(W, H);
        for (let i = 0; i < gid.data.length; i += 16) {
          const v = (Math.random() * 255) | 0;
          gid.data[i] = gid.data[i + 1] = gid.data[i + 2] = v;
          gid.data[i + 3] = 40;
        }
        gctx.putImageData(gid, 0, 0);

        ctx.clearRect(0, 0, W, H);
        ctx.drawImage(off, 0, 0);
        ctx.save();
        ctx.globalCompositeOperation = "source-atop";
        ctx.globalAlpha = 0.64;
        ctx.fillStyle = "#000";
        ctx.fillRect(0, 0, W, H);
        ctx.restore();

        ctx.save();
        ctx.globalCompositeOperation = "source-atop";
        const grd = ctx.createRadialGradient(W * 0.58, H * 0.26, 0, W * 0.58, H * 0.26, W * 0.3);
        grd.addColorStop(0, "rgba(255,255,255,0.19)");
        grd.addColorStop(0.3, "rgba(255,255,255,0.07)");
        grd.addColorStop(1, "rgba(255,255,255,0)");
        ctx.fillStyle = grd;
        ctx.fillRect(0, 0, W, H);
        ctx.restore();

        ctx.save();
        ctx.globalCompositeOperation = "source-atop";
        ctx.globalAlpha = 0.32;
        ctx.drawImage(grain, 0, 0);
        ctx.restore();
      };

      if (typeof window.requestIdleCallback === "function") {
        window.requestIdleCallback(run, { timeout: 1200 });
      } else {
        setTimeout(run, 50);
      }
    };

    return () => {
      cancelled = true;
    };
  }, [coalImage]);

  // Scroll-linked fade + parallax (no perpetual rAF when idle)
  useEffect(() => {
    const canvas = canvasRef.current;
    const wrapper = wrapperRef.current;
    if (!canvas || !wrapper) return;

    const DESKTOP_MAX = 64;
    const SCRUB_EASE = 0.18;
    let currentY = 0;
    let targetY = 0;
    let rafId = null;
    let reducedMotion = false;

    const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const syncMotionPref = () => {
      reducedMotion = motionQuery.matches;
      if (reducedMotion) {
        currentY = 0;
        targetY = 0;
        canvas.style.transform = "translate3d(0, 0, 0)";
      }
    };
    syncMotionPref();
    const onMotionChange = () => syncMotionPref();
    if (motionQuery.addEventListener) motionQuery.addEventListener("change", onMotionChange);
    else motionQuery.addListener(onMotionChange);

    const getMaxParallax = () => {
      const w = window.innerWidth;
      if (w <= 680) return DESKTOP_MAX * 0.3;
      if (w <= 1024) return DESKTOP_MAX * 0.5;
      return DESKTOP_MAX;
    };

    const update = () => {
      rafId = null;
      const vh = window.innerHeight;
      const scrollY = window.scrollY;
      const fadeStart = vh * 0.9;
      const fadeEnd = vh * 1.4;
      const opacity =
        scrollY <= fadeStart
          ? 1
          : scrollY >= fadeEnd
            ? 0
            : 1 - (scrollY - fadeStart) / (fadeEnd - fadeStart);
      wrapper.style.opacity = String(opacity);

      if (!reducedMotion) {
        const progress = Math.min(1, Math.max(0, scrollY / vh));
        targetY = progress * getMaxParallax();
        currentY += (targetY - currentY) * SCRUB_EASE;
        if (Math.abs(targetY - currentY) < 0.05) currentY = targetY;
        canvas.style.transform = `translate3d(0, ${currentY.toFixed(2)}px, 0)`;
        if (currentY !== targetY) {
          rafId = requestAnimationFrame(update);
        }
      }
    };

    const onScroll = () => {
      if (rafId == null) rafId = requestAnimationFrame(update);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    update();

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (rafId != null) cancelAnimationFrame(rafId);
      if (motionQuery.removeEventListener) motionQuery.removeEventListener("change", onMotionChange);
      else motionQuery.removeListener(onMotionChange);
    };
  }, []);

  return createPortal(
    <div
      ref={wrapperRef}
      style={{
        position: "fixed",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 2,
        pointerEvents: "none",
      }}
    >
      <canvas
        ref={canvasRef}
        width={512}
        height={512}
        className="coal-parallax-canvas"
        style={{
          width: "51.88vw",
          height: "51.88vw",
          pointerEvents: "none",
          flexShrink: 0,
          willChange: "transform",
          transform: "translate3d(0, 0, 0)",
          backfaceVisibility: "hidden",
        }}
      />
    </div>,
    document.body
  );
}

/* ─────────────────────────────────────────────
   HOW IT WORKS — Animated SVG journey path
───────────────────────────────────────────── */
const SVG_W = 1200, SVG_H = 380;
const DOT_DELAYS  = [0.42, 0.90, 1.38, 1.86, 2.34];
const TEXT_DELAYS = [0.58, 1.06, 1.54, 2.02, 2.50];

function WorldMapBg({ sectionRef, mapImage }) {
  const wrapRef = useRef(null);
  const src =
    typeof mapImage === "string" && mapImage.trim()
      ? mapImage.trim()
      : "/world-map.svg";

  useEffect(() => {
    const wrap = wrapRef.current;
    const section = sectionRef?.current;
    if (!wrap || !section) return undefined;
    if (typeof IntersectionObserver === "undefined") {
      wrap.style.transform = "translateY(-50%)";
      return undefined;
    }

    let rafId = null;
    let listening = false;

    const update = () => {
      rafId = null;
      const rect = section.getBoundingClientRect();
      const total = rect.height + window.innerHeight;
      const entered = window.innerHeight - rect.top;
      const progress = Math.max(0, Math.min(1, entered / total));
      const y = (progress - 0.5) * 100;
      wrap.style.transform = `translateY(calc(-50% + ${y.toFixed(2)}px))`;
    };
    const onScroll = () => {
      if (rafId == null) rafId = requestAnimationFrame(update);
    };
    const startListening = () => {
      if (listening) return;
      listening = true;
      window.addEventListener("scroll", onScroll, { passive: true });
      window.addEventListener("resize", onScroll, { passive: true });
      rafId = requestAnimationFrame(update);
    };
    const stopListening = () => {
      if (!listening) return;
      listening = false;
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (rafId != null) cancelAnimationFrame(rafId);
      rafId = null;
    };

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) startListening();
        else stopListening();
      },
      { rootMargin: "200px 0px" }
    );
    io.observe(section);

    return () => {
      io.disconnect();
      stopListening();
    };
  }, [sectionRef]);

  return (
    <div
      ref={wrapRef}
      aria-hidden="true"
      className="how-map-wrap"
    >
      <img
        className="how-map-img"
        src={src}
        alt=""
        width="1280"
        height="643"
        loading="lazy"
        decoding="async"
      />
    </div>
  );
}

function HowItWorksSection() {
  const { content } = useContent();
  const how = content.howItWorks;
  const HOW_STEPS = how.steps || [];
  const sectionRef = useRef(null);
  const [drawn, setDrawn] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setDrawn(true); observer.disconnect(); } },
      { threshold: 0.05, rootMargin: '0px 0px -50px 0px' }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="how-it-works" className="how-section" ref={sectionRef}>
      <WorldMapBg sectionRef={sectionRef} mapImage={how.mapImage} />
      <div className="how-header">
        <p className="how-eyebrow">{how.eyebrow}</p>
        <h2 className="how-heading">{how.headingLine1}<br />{how.headingLine2}</h2>
        <p className="how-desc">{how.desc}</p>
      </div>

      <div className="how-chart">
        <svg
          className="how-svg"
          viewBox={`0 0 ${SVG_W} ${SVG_H}`}
          preserveAspectRatio="none"
          fill="none"
          overflow="visible"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M -20,200 C 40,200 80,270 130,270 C 230,270 270,305 340,305 C 450,305 500,200 570,200 C 670,200 720,115 800,115 C 900,115 960,40 1010,40 C 1080,40 1160,10 1220,-5"
            stroke="#2a7fd4"
            strokeWidth="3"
            strokeLinecap="round"
            fill="none"
            className={drawn ? 'how-path how-path--drawn' : 'how-path'}
          />
        </svg>

        {HOW_STEPS.map((step, i) => {
          const dotLeft = `${((step.svgX / SVG_W) * 100).toFixed(2)}%`;
          const dotTop  = `${(step.svgY / SVG_H) * 100}%`;
          const flipLeft = false;
          const textStyle = {
            ...(flipLeft ? { left: 'auto', right: '14px' } : {}),
            ...(step.textAbove ? { top: 'auto', bottom: '14px' } : {}),
          };
          return (
            <div key={i} className="how-node" style={{ left: dotLeft, top: dotTop }}>
              <div
                className="how-dot"
                style={{
                  opacity: drawn ? 1 : 0,
                  transform: drawn
                    ? "translate(-50%,-50%) scale(1)"
                    : "translate(-50%,-50%) scale(0)",
                  transition: `opacity 1.2s ease ${DOT_DELAYS[i] || 0}s, transform 1.4s cubic-bezier(0.4,0,0.2,1) ${DOT_DELAYS[i] || 0}s`,
                }}
              />
              <div
                className={`how-step-text${drawn ? " is-drawn" : ""}`}
                style={{
                  ...textStyle,
                  transitionDelay: drawn ? `${TEXT_DELAYS[i] || 0}s` : "0s",
                }}
              >
                <span className="how-step-num-bg">{step.num}</span>
                <div className="how-step-body">
                  <h3 className="how-step-title">{step.title}</h3>
                  <p className="how-step-desc">{step.desc}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Mobile-only: simple step list replacing the SVG chart */}
      <div className="how-steps-mobile">
        {HOW_STEPS.map((step, i) => (
          <div key={i} className="how-step-mobile">
            <span className="how-step-mobile-num">0{i + 1}</span>
            <h3 className="how-step-mobile-title">{step.title}</h3>
            <p className="how-step-mobile-desc">{step.desc}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   PROBLEM SECTION — 3 cards, blue on hover
───────────────────────────────────────────── */
function ProblemSection() {
  const { content } = useContent();
  const problem = content.problem;
  const PROBLEM_CARDS = problem.cards || [];
  const stats = problem.stats || [];
  const cardsRef = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = cardsRef.current;
    if (!el) return undefined;
    const reduced =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      setInView(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="about" className="problem-section">
      <h2 className="problem-heading">{problem.heading}</h2>
      <div className="problem-cards" ref={cardsRef}>
        {PROBLEM_CARDS.map((card, i) => (
          <div
            className={`problem-card${inView ? " is-inview" : ""}`}
            key={card.title}
            style={{ transitionDelay: inView ? `${i * 0.18}s` : "0s" }}
          >
            <h3 className="problem-card-title">{card.title}</h3>
            <p className="problem-card-desc">{card.desc}</p>
          </div>
        ))}
      </div>

      <div className="problem-stat-line">
        {stats.map((stat, i) => (
          <React.Fragment key={stat.label}>
            {i > 0 && <span className="problem-stat-divider" />}
            <span className="problem-stat-val">{stat.value}</span>
            <span className="problem-stat-lbl">{stat.label}</span>
          </React.Fragment>
        ))}
      </div>

    </section>
  );
}

/* ─────────────────────────────────────────────
   NAVBAR
───────────────────────────────────────────── */
function sortActiveLinks(links) {
  return [...(links || [])]
    .filter((l) => !(l.active === false || l.active === "false"))
    .sort((a, b) => Number(a.order || 0) - Number(b.order || 0));
}

function Navbar({ onOpenConsultation }) {
  const { content } = useContent();
  const nav = content.nav;
  const site = content.site;
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => { setScrolled(window.scrollY > 60); setMenuOpen(false); };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const close = () => setMenuOpen(false);
  const topLinks = sortActiveLinks(nav.links);
  const solutions = sortActiveLinks(nav.solutions);
  const connect = sortActiveLinks(nav.connect);
  const mobileLinks = sortActiveLinks(nav.mobileLinks);
  const solutionsLabel = nav.solutionsLabel || "Solutions";
  const connectLabel = nav.connectLabel || "Connect";

  // Keep Solutions / Connect dropdowns in the same visual slots as before
  const recovery = topLinks.find((l) => /recovery/i.test(l.label)) || topLinks[0];
  const network = topLinks.find((l) => /network/i.test(l.label)) || topLinks[1];
  const whyUs = topLinks.find((l) => /why/i.test(l.label)) || topLinks[2];
  const academy = topLinks.find((l) => /academy/i.test(l.label)) || topLinks[3];

  const handleNavClick = (e, href) => {
    const target = (href || "").trim();
    if (!target || target === "#") {
      e.preventDefault();
      return;
    }

    if (/^https?:\/\//i.test(target) || target.startsWith("mailto:") || target.startsWith("tel:")) {
      return;
    }

    if (target.startsWith("/page/")) {
      e.preventDefault();
      close();
      navigate(target);
      return;
    }

    if (target.includes("#")) {
      const hash = target.slice(target.indexOf("#") + 1);
      e.preventDefault();
      close();
      if (hash === "consultation") {
        if (location.pathname !== "/") navigate("/");
        setTimeout(() => onOpenConsultation?.(), 0);
        return;
      }
      if (location.pathname !== "/") {
        navigate(`/#${hash}`);
        return;
      }
      const el =
        (hash === "contact" ? document.getElementById("partner") : null) ||
        document.getElementById(hash) ||
        document.getElementById("contact");
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        window.history.replaceState(null, "", `/#${hash}`);
      }
    }
  };

  return (
    <>
      <nav className={`navbar${scrolled ? " navbar--scrolled" : ""}`}>
        <div className="navbar-brand" role="link" tabIndex={0} onClick={() => navigate("/")} onKeyDown={(e) => e.key === "Enter" && navigate("/")}>
          <img src={site.logo || "/logo-icon.png"} alt={site.name || "OilOnWheelz"} className="navbar-logo-img" width="66" height="32" decoding="async" fetchpriority="low" />
          <span className="navbar-wordmark">{site.wordmark || "OILONWHEELZ"}</span>
        </div>
        <ul className="navbar-links">
          {recovery && <li><a href={recovery.href} onClick={(e) => handleNavClick(e, recovery.href)}>{recovery.label}</a></li>}
          <li className="navbar-dropdown-item">
            <span className="navbar-dropdown-trigger">{solutionsLabel} <span className="navbar-caret">▾</span></span>
            <ul className="navbar-dropdown-menu">
              {solutions.map((item) => (
                <li key={item.label}><a href={item.href || "#"} onClick={(e) => handleNavClick(e, item.href)}>{item.label}</a></li>
              ))}
            </ul>
          </li>
          {network && <li><a href={network.href} onClick={(e) => handleNavClick(e, network.href)}>{network.label}</a></li>}
          {whyUs && <li><a href={whyUs.href} onClick={(e) => handleNavClick(e, whyUs.href)}>{whyUs.label}</a></li>}
          {academy && <li><a href={academy.href || "#"} onClick={(e) => handleNavClick(e, academy.href)}>{academy.label}</a></li>}
          <li className="navbar-dropdown-item">
            <span className="navbar-dropdown-trigger">{connectLabel} <span className="navbar-caret">▾</span></span>
            <ul className="navbar-dropdown-menu">
              {connect.map((item) => (
                <li key={item.label}><a href={item.href || "#"} onClick={(e) => handleNavClick(e, item.href)}>{item.label}</a></li>
              ))}
            </ul>
          </li>
        </ul>
        <button
          className={`navbar-hamburger${menuOpen ? " navbar-hamburger--open" : ""}`}
          onClick={() => setMenuOpen(o => !o)}
          aria-label="Toggle menu"
        >
          <span /><span /><span />
        </button>
      </nav>

      {menuOpen && (
        <div className="mobile-nav-overlay" onClick={close}>
          <ul className="mobile-nav-links" onClick={e => e.stopPropagation()}>
            {mobileLinks.map((item, i) => {
              const isDividerBefore =
                i > 0 &&
                /contact|partner|collection/i.test(item.label) &&
                !/contact|partner|collection/i.test(mobileLinks[i - 1].label);
              return (
                <React.Fragment key={`${item.label}-${i}`}>
                  {isDividerBefore && <li className="mobile-nav-divider" />}
                  <li><a href={item.href || "#"} onClick={(e) => handleNavClick(e, item.href)}>{item.label}</a></li>
                </React.Fragment>
              );
            })}
          </ul>
        </div>
      )}
    </>
  );
}

/* ─────────────────────────────────────────────
   WHY US SECTION — "Built on real infrastructure"
───────────────────────────────────────────── */
function WhyUsSection() {
  const { content } = useContent();
  const why = content.whyUs;
  const WHY_CARDS = why.cards || [];

  return (
    <section id="why-us" className="why-section">
      <div className="why-inner">
        <div className="why-header">
          <p className="why-label">{why.label}</p>
          <h2 className="why-heading">{why.headingLine1}<br />{why.headingLine2}</h2>
          <p className="why-desc">{why.desc}</p>
        </div>
        <div className="why-cards">
          {WHY_CARDS.map((c) => (
            <div className="why-card" key={c.title}>
              <div className="why-card-img">
                <picture>
                  {webpSibling(c.image) ? (
                    <source type="image/webp" srcSet={webpSibling(c.image)} />
                  ) : null}
                  <img
                    className="why-card-photo"
                    src={c.image}
                    alt=""
                    width="800"
                    height="536"
                    loading="lazy"
                    decoding="async"
                  />
                </picture>
                {c.gradient ? (
                  <div className="why-card-img-shade" style={{ backgroundImage: c.gradient }} aria-hidden="true" />
                ) : null}
                {c.icon ? <img src={c.icon} alt="" width="40" height="40" loading="lazy" decoding="async" /> : null}
              </div>
              <div className="why-card-body">
                <h3 className="why-card-title">{c.title}</h3>
                <p className="why-card-desc">{c.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   SCROLL 4 — Numbers speak / Stats section
───────────────────────────────────────────── */
// All counters run for the same 2 s → higher numbers increment faster visually
function AnimatedNumber({ num, suffix, comma, started }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!started) return;
    const DURATION = 2000;
    let startTime = null;
    let raf;

    const step = (ts) => {
      if (!startTime) startTime = ts;
      const progress = Math.min((ts - startTime) / DURATION, 1);
      const eased    = 1 - Math.pow(1 - progress, 3); // easeOutCubic
      const current  = Math.floor(eased * num);
      setCount(current);
      if (progress < 1) raf = requestAnimationFrame(step);
      else setCount(num);
    };

    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [started, num]);

  const display = comma ? count.toLocaleString() : String(count);
  return <>{display}{suffix}</>;
}

function NumbersSpeakSection() {
  const { content } = useContent();
  const numbers = content.numbers;
  const STAT_CARDS = numbers.stats || [];
  const sectionRef = useRef(null);
  const [started, setStarted] = useState(false);
  const [loadBlinds, setLoadBlinds] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStarted(true);
          setLoadBlinds(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px 0px", threshold: 0.05 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="numbers" className="numbers-section" ref={sectionRef}>
      {/* GradientBlinds (ogl) only after section approaches viewport — was blocking LCP */}
      <div style={{ position: "absolute", inset: 0, zIndex: 0 }} aria-hidden="true">
        {loadBlinds && (
          <Suspense fallback={null}>
            <GradientBlinds
              gradientColors={['#04060f', '#081528', '#0c1e3a', '#060c18', '#030508']}
              angle={20}
              noise={0.10}
              blindCount={10}
              blindMinWidth={80}
              mouseDampening={0.20}
              spotlightRadius={0.55}
              spotlightSoftness={1.3}
              spotlightOpacity={0.85}
              distortAmount={0}
              shineDirection="left"
              mixBlendMode="normal"
            />
          </Suspense>
        )}
      </div>

      <div className="numbers-inner" style={{ position: "relative", zIndex: 1 }}>

        {/* Top: headline left, quote right */}
        <div className="numbers-top">
          <div className="numbers-left">
            <h2 className="numbers-heading">
              {numbers.headingLine1}<br />{numbers.headingLine2}
            </h2>
            <p className="numbers-sub">{numbers.sub}</p>
          </div>
          <div className="numbers-right">
            <blockquote className="numbers-quote">{numbers.quote}</blockquote>
          </div>
        </div>

        {/* Stat cards */}
        <div className="numbers-cards">
          {STAT_CARDS.map((s) => (
            <div className={`numbers-card${s.accent ? " numbers-card--accent" : ""}`} key={s.label}>
              <span className="numbers-card-label">{s.label}</span>
              <span className="numbers-card-value">
                <AnimatedNumber num={s.num} suffix={s.suffix} comma={s.comma} started={started} />
              </span>
            </div>
          ))}
        </div>

        {/* Footer tagline */}
        <p className="numbers-tagline">{numbers.tagline}</p>

      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   SITE FOOTER — CTA card + 4-column links
───────────────────────────────────────────── */
function LayerVisual({ src, onLoad }) {
  const webp = webpSibling(src);
  const frame = { width: "100%", height: "100%", objectFit: "cover", borderRadius: "20px", display: "block" };
  return (
    <picture style={{ width: "100%", height: "100%", display: "block" }}>
      {webp ? <source type="image/webp" srcSet={webp} /> : null}
      <img
        src={src}
        alt=""
        width="1280"
        height="720"
        loading="lazy"
        decoding="async"
        onLoad={onLoad}
        style={frame}
      />
    </picture>
  );
}

function LayerCarousel() {
  const { content } = useContent();
  const LAYERS = content.layers?.items || [];
  const SLIDE_DURATION = content.layers?.slideDuration || 5500;
  const [active, setActive] = useState(0);
  const [progress, setProgress] = useState(0);
  const [animKey, setAnimKey] = useState(0);
  const [mediaReady, setMediaReady] = useState(false);
  const [mediaLoaded, setMediaLoaded] = useState(false);
  const sectionRef = useRef(null);
  const pausedRef = useRef(false);
  const progressRef = useRef(0);
  const startRef = useRef(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setMediaReady(true);
          observer.disconnect();
        }
      },
      { rootMargin: "80px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setMediaLoaded(false);
  }, [active]);

  useEffect(() => {
    if (!LAYERS.length || !mediaReady || !mediaLoaded) return;
    setProgress(0);
    progressRef.current = 0;
    pausedRef.current = false;
    startRef.current = Date.now();
    let elapsed = 0;
    const tick = setInterval(() => {
      if (pausedRef.current) return;
      elapsed = Date.now() - startRef.current;
      const pct = Math.min((elapsed / SLIDE_DURATION) * 100, 100);
      progressRef.current = pct;
      setProgress(pct);
      if (pct >= 100) {
        clearInterval(tick);
        setActive(prev => (prev + 1) % LAYERS.length);
        setAnimKey(k => k + 1);
      }
    }, 32);
    return () => clearInterval(tick);
  }, [active, LAYERS.length, SLIDE_DURATION, mediaReady, mediaLoaded]);

  const handleTab = (i) => {
    setActive(i);
    setAnimKey(k => k + 1);
  };

  const handleTabMouseEnter = (i) => {
    if (i === active) {
      pausedRef.current = true;
    }
  };

  const handleTabMouseLeave = (i) => {
    if (i === active) {
      startRef.current = Date.now() - (progressRef.current / 100) * SLIDE_DURATION;
      pausedRef.current = false;
    }
  };

  if (!LAYERS.length) return null;

  return (
    <section id="layers" className="layer-section" ref={sectionRef}>
      <div className="layer-tabs">
        {LAYERS.map((layer, i) => (
          <button key={i} className={`layer-tab${i < active ? ' layer-tab--past' : i === active ? ' layer-tab--done' : ''}`} onClick={() => handleTab(i)} onMouseEnter={() => handleTabMouseEnter(i)} onMouseLeave={() => handleTabMouseLeave(i)}>
            <div className="layer-tab-inner">
              <span className="layer-tab-label">{layer.tab}</span>
              <span className="layer-tab-arrow">→</span>
            </div>
            <div className="layer-tab-bar">
              <div className="layer-tab-bar-fill" style={{
                width: i < active ? '100%' : i === active ? `${progress}%` : '0%',
              }}/>
            </div>
          </button>
        ))}
      </div>

      <div className="layer-body">
        <div className="layer-left-stack">
          {LAYERS.map((layer, li) => (
            <div
              key={li}
              className={`layer-left${li === active ? ' layer-left--active' : ''}`}
              aria-hidden={li !== active}
              style={li === active ? { animationName: 'layerSlideIn' } : {}}
            >
              <h2 className="layer-title">{layer.title}</h2>
              {layer.subtitle && <p className="layer-subtitle">{layer.subtitle}</p>}
              <p className="layer-copy">{layer.copy}</p>
              <div className="layer-blocks">
                {(layer.blocks || []).map((block, i) => (
                  <div key={i} className="layer-block">
                    <span className="layer-block-num">0{i + 1}</span>
                    <div className="layer-block-body">
                      <h3 className="layer-block-title">{block.title}</h3>
                      <p className="layer-block-desc">{block.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="layer-right">
          <div className="layer-visual" key={`v-${animKey}`}>
            {mediaReady ? (
              <LayerVisual src={LAYERS[active]?.media} onLoad={() => setMediaLoaded(true)} />
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   IMPACT ACCORDION — 5-card horizontal expand
───────────────────────────────────────────── */
function ImpactSection() {
  const { content } = useContent();
  const impact = content.impact;
  const IMPACT_CARDS = impact.cards || [];
  const [active, setActive] = useState(0);

  return (
    <section id="impact" className="impact-section">
      <div className="impact-inner">
      <div className="impact-header">
        <h2 className="impact-heading">{impact.headingLine1}<br />{impact.headingLine2}</h2>
        <p className="impact-intro">{impact.intro}</p>
      </div>

      <div className="impact-cards">
        {IMPACT_CARDS.map((card, i) => (
          <div
            key={i}
            className={`impact-card${active === i ? ' impact-card--active' : ''}`}
            onMouseEnter={() => setActive(i)}
          >
            <div className="impact-card-collapsed">
              <span className="impact-card-num">0{i + 1}</span>
              <span className="impact-card-collapsed-title">{card.title}</span>
            </div>

            <div className="impact-card-expanded">
              <span className="impact-card-num-exp">0{i + 1}</span>
              <div className="impact-card-body">
                <h3 className="impact-card-title">{card.title}</h3>
                <p className="impact-card-desc">{card.desc}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   CONSULTATION MODAL — Schedule a Consultation form
───────────────────────────────────────────── */
function ConsultationModal({ onClose }) {
  const { content } = useContent();
  const modal = content.consultationModal;
  const [form, setForm] = useState({ name: '', company: '', designation: '', phone: '', email: '', brief: '' });
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) =>
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError('');
    try {
      await submitForm('consultation', form);
      setSubmitted(true);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card modal-card--wide" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose} aria-label="Close">×</button>

        {submitted ? (
          <div className="modal-success">
            <div className="modal-success-icon">✓</div>
            <h3 className="modal-success-heading">{modal.successHeading}</h3>
            <p className="modal-success-sub">{modal.successSub}</p>
          </div>
        ) : (
          <>
            <p className="modal-eyebrow">{modal.eyebrow}</p>
            <h2 className="modal-heading">{modal.heading}</h2>
            <p className="modal-sub">{modal.sub}</p>
            {error && <p className="modal-sub" style={{ color: '#ff8f8f' }}>{error}</p>}

            <form className="modal-form" onSubmit={handleSubmit}>
              <div className="modal-row">
                <div className="modal-field">
                  <label htmlFor="c-name">Name</label>
                  <input id="c-name" name="name" type="text" placeholder={modal.fields?.namePlaceholder} value={form.name} onChange={handleChange} required />
                </div>
                <div className="modal-field">
                  <label htmlFor="c-company">Company</label>
                  <input id="c-company" name="company" type="text" placeholder={modal.fields?.companyPlaceholder} value={form.company} onChange={handleChange} required />
                </div>
              </div>

              <div className="modal-row">
                <div className="modal-field">
                  <label htmlFor="c-designation">Designation</label>
                  <input id="c-designation" name="designation" type="text" placeholder={modal.fields?.designationPlaceholder} value={form.designation} onChange={handleChange} required />
                </div>
                <div className="modal-field">
                  <label htmlFor="c-phone">Phone Number</label>
                  <div className="modal-phone-wrap">
                    <span className="modal-phone-code">{modal.phoneCode || '+91'}</span>
                    <input id="c-phone" name="phone" type="tel" placeholder={modal.fields?.phonePlaceholder} value={form.phone} onChange={handleChange} required />
                  </div>
                </div>
              </div>

              <div className="modal-field">
                <label htmlFor="c-email">Email</label>
                <input id="c-email" name="email" type="email" placeholder={modal.fields?.emailPlaceholder} value={form.email} onChange={handleChange} required />
              </div>

              <div className="modal-field">
                <label htmlFor="c-brief">Brief Requirement</label>
                <textarea id="c-brief" name="brief" placeholder={modal.fields?.briefPlaceholder} value={form.brief} onChange={handleChange} rows={4} className="modal-textarea" />
              </div>

              <button type="submit" className="modal-submit" disabled={sending}>{sending ? 'Sending…' : modal.submitLabel}</button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   PARTNER MODAL — form popup
───────────────────────────────────────────── */
function PartnerModal({ onClose }) {
  const { content } = useContent();
  const modal = content.partnerModal;
  const [form, setForm]           = useState({ name: '', phone: '', email: '' });
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) =>
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError('');
    try {
      await submitForm('partner', form);
      setSubmitted(true);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose} aria-label="Close">×</button>

        {submitted ? (
          <div className="modal-success">
            <div className="modal-success-icon">✓</div>
            <h3 className="modal-success-heading">{modal.successHeading}</h3>
            <p className="modal-success-sub">{modal.successSub}</p>
          </div>
        ) : (
          <>
            <p className="modal-eyebrow">{modal.eyebrow}</p>
            <h2 className="modal-heading">{modal.heading}</h2>
            <p className="modal-sub">{modal.sub}</p>
            {error && <p className="modal-sub" style={{ color: '#ff8f8f' }}>{error}</p>}

            <form className="modal-form" onSubmit={handleSubmit}>
              <div className="modal-field">
                <label htmlFor="m-name">Full Name</label>
                <input
                  id="m-name" name="name" type="text"
                  placeholder={modal.fields?.namePlaceholder}
                  value={form.name} onChange={handleChange} required
                />
              </div>

              <div className="modal-field">
                <label htmlFor="m-phone">Phone Number</label>
                <div className="modal-phone-wrap">
                  <span className="modal-phone-code">{modal.phoneCode || '+91'}</span>
                  <input
                    id="m-phone" name="phone" type="tel"
                    placeholder={modal.fields?.phonePlaceholder}
                    value={form.phone} onChange={handleChange} required
                  />
                </div>
              </div>

              <div className="modal-field">
                <label htmlFor="m-email">Email Address</label>
                <input
                  id="m-email" name="email" type="email"
                  placeholder={modal.fields?.emailPlaceholder}
                  value={form.email} onChange={handleChange} required
                />
              </div>

              <button type="submit" className="modal-submit" disabled={sending}>{sending ? 'Sending…' : modal.submitLabel}</button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   CTA SECTION — dark card above FAQ
───────────────────────────────────────────── */
function CTASection() {
  const { content } = useContent();
  const cta = content.cta;
  const [modalOpen, setModalOpen] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVideoReady(true);
          observer.disconnect();
        }
      },
      { rootMargin: "0px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <section id="partner" className="cta-section" ref={sectionRef}>
        <div className="cta-inner">
          {videoReady && (
            <video
              className="cta-bg-video"
              src={cta.video || "/oilGIF.mp4"}
              autoPlay
              loop
              muted
              playsInline
              preload="none"
              aria-hidden="true"
            />
          )}
          <div className="cta-overlay" />
          <div className="cta-content">
            <p className="cta-eyebrow">{cta.eyebrow}</p>
            <h2 className="cta-heading">{cta.heading}</h2>
            <p className="cta-sub">{cta.sub}</p>
            <button className="cta-btn" onClick={() => setModalOpen(true)}>
              {cta.buttonLabel}
            </button>
          </div>
        </div>
      </section>

      {modalOpen && <PartnerModal onClose={() => setModalOpen(false)} />}
    </>
  );
}

function FAQSection() {
  const { content } = useContent();
  const faq = content.faq;
  const FAQ_ITEMS = faq.items || [];
  const [openIdx, setOpenIdx] = useState(null);
  return (
    <section id="faq" className="faq-section">
      <p className="faq-eyebrow">{faq.eyebrow}</p>
      <h2 className="faq-heading">{faq.headingLine1}<br />{faq.headingLine2}</h2>
      <div className="faq-list">
        {FAQ_ITEMS.map((item, i) => (
          <div
            key={i}
            className={`faq-item${openIdx === i ? ' faq-item--open' : ''}`}
            onClick={() => setOpenIdx(openIdx === i ? null : i)}
          >
            <div className="faq-item-header">
              <span className="faq-item-q">{item.q}</span>
              <span className="faq-item-icon">{openIdx === i ? '×' : '+'}</span>
            </div>
            {openIdx === i && <p className="faq-item-a">{item.a}</p>}
          </div>
        ))}
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   OIL FLOW DIAGRAM — vertical lifecycle card
───────────────────────────────────────────── */
function OilFlowDiagram() {
  const { content } = useContent();
  const FLOW_STEPS = content.flowSteps || [];
  return (
    <div className="flow-list">
      {FLOW_STEPS.map((step, i) => (
        <React.Fragment key={i}>
          <div className="flow-step">
            <div className="flow-icon" style={{ animationDelay: `${i * 2}s` }}>{FLOW_ICONS[step.iconKey] || FLOW_ICONS.crude}</div>
            <div className="flow-step-body">
              <span className="flow-step-title" style={{ animationDelay: `${i * 2}s` }}>{step.title}</span>
              <span className="flow-step-desc" style={{ animationDelay: `${i * 2}s` }}>{step.desc}</span>
            </div>
          </div>
          {i < FLOW_STEPS.length - 1 && (
            <div className="flow-arrow">
              <svg width="80" height="14" viewBox="0 0 80 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <line x1="0" y1="7" x2="66" y2="7" stroke="white" strokeOpacity="0.18" strokeWidth="1" strokeDasharray="3 5"/>
                <path d="M60 2 L72 7 L60 12" stroke="white" strokeOpacity="0.45" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────
   APP ROOT
───────────────────────────────────────────── */
function App() {
  const { content } = useContent();
  const hero = content.hero;
  const [consultOpen, setConsultOpen] = useState(false);
  const [fxReady, setFxReady] = useState(false);

  useEffect(() => {
    const hash = window.location.hash.replace(/^#/, "");
    if (hash === "consultation") {
      setConsultOpen(true);
    }
  }, []);

  // Heavy FX (Pixi / WebGL) only after a real user gesture — no idle timeout.
  // Lab traces never interact, so they skip 200KB+ of unused JS and perpetual rAF.
  useEffect(() => {
    const prefersReduced =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const saveData = navigator.connection?.saveData;
    const slowNet =
      typeof navigator.connection?.effectiveType === "string" &&
      /2g/.test(navigator.connection.effectiveType);

    if (prefersReduced || saveData || slowNet) return undefined;

    let armed = false;

    const start = () => {
      if (armed) return;
      armed = true;
      setFxReady(true);
      window.removeEventListener("scroll", start);
      window.removeEventListener("pointerdown", start);
      window.removeEventListener("keydown", start);
      window.removeEventListener("touchstart", start);
    };

    window.addEventListener("scroll", start, { passive: true, once: true });
    window.addEventListener("pointerdown", start, { passive: true, once: true });
    window.addEventListener("keydown", start, { once: true });
    window.addEventListener("touchstart", start, { passive: true, once: true });

    return () => {
      window.removeEventListener("scroll", start);
      window.removeEventListener("pointerdown", start);
      window.removeEventListener("keydown", start);
      window.removeEventListener("touchstart", start);
    };
  }, []);

  return (
    <div className="app">
      {/* ── Layer 0: FBM noise shader (lazy WebGL, after LCP) ── */}
      {fxReady && (
        <Suspense fallback={null}>
          <BackgroundCanvas />
        </Suspense>
      )}

      {/* ── Layer 1: Coal canvas (deferred with other FX so LCP paints first) ── */}
      {fxReady && <CoalImageCanvas />}

      {/* Film-grain is deferred with other FX so it does not animate during first paint */}
      {fxReady && <div className="grain-overlay" aria-hidden="true" />}

      {/* ── Layer 2: PixiJS particles (deferred, z 2) ── */}
      {fxReady && <PixiBackground />}

      {/* ── Navbar ── */}
      <Navbar onOpenConsultation={() => setConsultOpen(true)} />

      <main id="main-content">
      {/* ── Hero ── */}
      <section className="hero">

        {/* Left: headline + CTA + stats */}
        <div className="hero-content">
          <p className="hero-eyebrow">
            {hero.eyebrow}
          </p>

          {/* LCP element: keep visible immediately — do not start at opacity 0 */}
          <h1>
            {hero.titleLine1}<br />{hero.titleLine2}
          </h1>

          <p className="hero-sub">
            {hero.body}
          </p>

          <div className="hero-btns">
            <button className="btn-primary" onClick={() => setConsultOpen(true)}>{hero.primaryCta}</button>
            <button className="btn-secondary" onClick={() => {
              const href = hero.secondaryHref || "/#layers";
              const hash = href.includes("#") ? href.slice(href.indexOf("#") + 1) : "layers";
              const target = document.getElementById(hash);
              if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
            }}>{hero.secondaryCta}</button>
          </div>

          <div className="hero-flow-diagram">
            <OilFlowDiagram />
          </div>

        </div>

      </section>

      {/* ── PROBLEM CARDS — sits below hero, coal visible through transparent bg ── */}
      <ProblemSection />

      {/* ── LAYER CAROUSEL — 3-layer deep dive ── */}
      <LayerCarousel />

      {/* ── IMPACT ACCORDION ── */}
      <ImpactSection />

      {/* ── SCROLL 4 — Numbers speak / Stats ── */}
      <NumbersSpeakSection />

      {/* ── FOLD 3 — White content section ── */}
      <WhyUsSection />

      {/* ── HOW IT WORKS — animated SVG journey ── */}
      <HowItWorksSection />

      {/* ── CTA ── */}
      <div id="contact" aria-hidden="true" style={{ height: 0, overflow: "hidden" }} />
      <CTASection />

      {/* ── FAQ ── */}
      <FAQSection />
      </main>

      {/* ── FOOTER — link grid ── */}
      <SiteFooter onOpenConsultation={() => setConsultOpen(true)} />

      {/* ── CONSULTATION MODAL ── */}
      {consultOpen && <ConsultationModal onClose={() => setConsultOpen(false)} />}

    </div>
  );
}

export default App;
